import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { ProgrammeSummary } from '../../../../../types/entity.types';

import { setAccountabilityTemplate } from '../../../../../slices/programme/accountability/accountability.slice';
import { setCollections } from '../../../../../slices/programme/plan/plan.slice';
import { setSupportTemplate } from '../../../../../slices/programme/support/support.slice';
import { setTribeTemplate } from '../../../../../slices/programme/tribe/tribe.slice';

import { formatProgrammeDates, getClassNames, isProgrammeIncomplete } from '../../../../../utils/util';

import './programme-menu-section.scss';

type Props = {
  heading: string;
  programmes: ProgrammeSummary[];
  onNavigate?: (value: string) => void;
};

const ProgrammeMenuSection = ({ heading, programmes = [], onNavigate }: Props) => {
  const [open, setOpen] = useState(true);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { programmeExternalReference } = useParams();

  if (programmes.length === 0) return <Fragment />;

  const handleProgrammeClick = (programme: ProgrammeSummary, selected: boolean) => {
    if (selected) return;

    dispatch(setCollections([]));
    dispatch(setAccountabilityTemplate(undefined));
    dispatch(setSupportTemplate(undefined));
    dispatch(setTribeTemplate(undefined));

    const { externalReference, status } = programme;
    const url = `/programmes/${status}/${externalReference}/general/members?hasSubProgrammes=${!!programme.hasSubProgrammes}`;

    if (onNavigate) {
      onNavigate(url);
    } else {
      navigate(url);
    }
  };

  const renderMenuItems = () => {
    const copyOfProgrammes = structuredClone(programmes);

    copyOfProgrammes.sort((programmeOne, programmeTwo) => {
      const dateOne = new Date(programmeOne.runningFromDate);
      const dateTwo = new Date(programmeTwo.runningFromDate);

      if (dateOne > dateTwo) return 1;
      if (dateOne < dateTwo) return -1;
      return 0;
    });

    return copyOfProgrammes.map((programme, index) => {
      const selected = programme.externalReference === programmeExternalReference;

      return (
        <div
          className="programme-menu-section__item"
          key={index}
          onClick={() => handleProgrammeClick(programme, selected)}
          data-testid="menu-item"
        >
          <div className={getClassNames('programme-menu-section__item__name', { selected })}>
            <p>{programme.name}</p>
            {isProgrammeIncomplete(programme) && (
              <div className="programme-menu-section__item__icon" data-testid="incomplete-icon">
                <p>!</p>
              </div>
            )}
          </div>
          <p className={getClassNames('programme-menu-section__item__date', { selected })}>
            {formatProgrammeDates(programme.runningFromDate, programme.runningToDate)}
          </p>
        </div>
      );
    });
  };

  return (
    <div className="programme-menu-section">
      <div className="programme-menu-section__heading">
        <div className="programme-menu-section__heading__text">{heading}</div>
        <div className="programme-menu-section__heading__button" onClick={() => setOpen((prevState) => !prevState)}>
          {!open && <FontAwesomeIcon icon={faPlus} title="plus icon" />}
          {open && <FontAwesomeIcon icon={faMinus} title="minus icon" />}
        </div>
      </div>
      <div className={getClassNames('programme-menu-section__items', { open })}>{renderMenuItems()}</div>
    </div>
  );
};

export default ProgrammeMenuSection;
